<script setup lang="ts">
import Tabs from '@/components/Tabs/Tabs.vue';
import type { TabDefinition, TabSlots } from '@/components/Tabs/types.ts';
import { useTabs } from '@/components/Tabs/useTabs.ts';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { computed, toRef } from 'vue';
import BasePageTabContent from './BasePageTabContent.vue';
import { PageAction } from './types';
import Button from '@/components/buttons/Button.vue';

const props = defineProps<{
  tabs: TabDefinition | undefined;
  actions?: PageAction[];
  centered?: boolean;
}>();

defineSlots<TabSlots>();

const { activeTab, visibleTabs, selectByKey } = useTabs({
  tabs: toRef(() => props.tabs),
  urlControlled: true,
});

const justifyContent = computed(() => (props.centered ? 'center' : 'space-between'));
</script>

<template>
  <v-toolbar class="pageTabs" height="48">
    <Tabs :modelValue="activeTab" :tabs="visibleTabs" @select="selectByKey" />

    <template #append>
      <div v-if="actions?.length" class="actions">
        <template v-for="(action, index) in actions">
          <Tooltip
            v-if="!action.hidden"
            :key="index"
            position="bottom"
            :disabled="!(action.tooltip || action.text)"
          >
            <template #target="{ props: targetProps }">
              <span v-bind="targetProps" class="actionButton">
                <Button
                  :icon="action.icon"
                  size="small"
                  :disabled="action.disabled"
                  :to="action.to"
                  :title="action.text"
                  variant="primary"
                  @click="action.callback?.($event)"
                />
              </span>
            </template>
            <template #tooltip>{{ action.tooltip || action.text }}</template>
          </Tooltip>
        </template>
      </div>
    </template>
  </v-toolbar>

  <template v-for="(tab, key) in visibleTabs" :key="key">
    <BasePageTabContent
      v-if="!tab.unmountTab || activeTab === key"
      v-show="activeTab === key"
      :hideContentSheet="tab.hideContentSheet"
      :tabContent="key"
    >
      <slot :name="key" />
    </BasePageTabContent>
  </template>
</template>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/mixins';

.pageTabs {
  display: flex;
  width: 100%;

  justify-content: v-bind(justifyContent);
  background: rgb(var(--v-theme-secondary-lighten-5));

  @include mixins.shadow-bottom(5);
  &:after {
    bottom: -2px;
  }

  .actions {
    display: flex;
    justify-content: end;
    align-items: center;
  }
}

.actionButton:not(:last-child) {
  margin-right: 12px;
}
</style>
